import {
  OIDCLogin,
  OIDCLoginCallback,
  OIDCLogout,
  OIDCLogoutCallback,
  permissionsByEntity,
  PrivateRouteContent,
  useCheckAccountType,
} from '@cmg/auth';
import { apiTypes, ApplicationError, urlUtil } from '@cmg/common';
import React, { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import Loading from '../../common/components/indicators/loading/Loading';
import NotAccessible from '../../common/components/layout/not-accessible/NotAccessible';
import SupportRequestModal from '../../common/components/modals/support-request-modal/SupportRequestModal';
import useCertificateLibraryAccess from '../../common/hooks/useCertificateLibraryAccess';
import useCertificateReviewAccess from '../../common/hooks/useCertificateReviewAccess';
import useHasMyOrders from '../../common/hooks/useHasMyOrders';
import useInvestorDirectoryAccess from '../../common/hooks/useInvestorDirectoryAccess';
import routeFactory from '../../common/util/routeFactory';
import LoggedOutRoute from '../logged-out/LoggedOutRoute';
import { useHasOfferingAccess } from '../offering/hooks/useHasOfferingAccess';
import OfferingCreateRoute from '../offering/OfferingCreateRoute';
import { CmgEntityKeyProvider } from '../offering-side-panel/contexts/CmgEntityKeyContext';
import { FirmKeyOrIdProvider } from '../offering-side-panel/contexts/FirmKeyOrIdContext';
import { OfferingSidePanelContainer } from '../offering-side-panel/OfferingSidePanelContainer';
import CrmSelectModal from '../offering-side-panel/order-book/indication-activity/components/crm-selection/CrmSelectModal';

const BrowserNotificationsDocumentationRoute = lazy(
  () => import('../documentation/BrowserNotificationsDocumentationRoute')
);
const CalendarRoute = lazy(() => import('../calendar/CalendarRoute'));
const CertificateLibraryRoute = lazy(() => import('../certificate-library/CertificateLibraryPage'));
const InvestorDirectoryRoute = lazy(() => import('../investor-directory/InvestorDirectoryRoute'));
const InvitationWireResponseRoute = lazy(
  () =>
    import('../syndicate-wires/public-routes/invitation-wire-response/InvitationWireResponseRoute')
);

const MyOfferingsRoute = lazy(() => import('../my-offerings/MyOfferingsRoute'));
const MyOrdersRoute = lazy(() => import('../my-orders/MyOrdersRoute'));
const OfferingRoute = lazy(() => import('../offering/OfferingRoute'));

const SettingsRoute = lazy(() => import('../settings/SettingsRoute'));

const SharedDraftOfferingRoute = lazy(
  () => import('../shared-draft-offering/SharedDraftOfferingRoute')
);

type NoAccessProps = {
  isLoggedIn: boolean;
  subject?: string;
};

/**
 * NoAccessRedirect fallback redirect wrapper
 *
 * When the user is not logged in - redirect the user back
 * to the oidc login route url and set the location state to the current location.
 * When the user is logged in - show an error page.
 */
export const NoAccessRedirect: React.FC<NoAccessProps> = ({ isLoggedIn, subject }) => {
  const location = useLocation();

  // User is not logged in - redirect to login
  if (!isLoggedIn) {
    return (
      <Navigate
        to={routeFactory.oidcLogin.getUrlPath()}
        state={routeFactory.oidcLogin.getRouteState({
          location: location,
        })}
        replace
      />
    );
  }

  // User is logged in but does not have permissions to view this route
  return <NotAccessible subject={subject} />;
};

const ErrorRoute: React.FC = () => {
  const location = useLocation();
  const query = urlUtil.queryParse<{
    errorCode?: apiTypes.ServiceErrorCode;
    returnUrl?: string;
  }>(location.search);

  return (
    <ApplicationError
      errorCode={query.errorCode}
      returnUrl={query.returnUrl ?? routeFactory.root.routePath}
    />
  );
};

/**
 * Responsible for application level routes. Most routes in our sitemap should be represented here.
 * Occasionally we will use the ReactRouter v4 feature of nesting <Routes> further down the component tree.
 */
export const RootRouter: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasMyOrders = useHasMyOrders();
  const isSellSide = useCheckAccountType('SELL_SIDE');
  const hasOfferingAccess = useHasOfferingAccess();
  const hasMyOfferings = hasOfferingAccess && isSellSide && !hasMyOrders;
  const {
    canRenderRoute: canRenderCertificateLibraryRoute,
    permissions: certificateLibraryPermissions,
  } = useCertificateLibraryAccess();
  const {
    canRenderRoute: canRenderCertificateReviewRoute,
    permissions: certificateReviewPermissions,
  } = useCertificateReviewAccess();
  const { canRenderRoute: canRenderInvestorDirectoryRoute } = useInvestorDirectoryAccess();

  return (
    <React.Fragment>
      <Routes>
        {/* OIDC flow */}
        <Route path={routeFactory.loggedOut.routePath} element={<LoggedOutRoute />} />
        <Route
          path={routeFactory.oidcLogin.routePath}
          element={
            <OIDCLogin
              locationState={location.state}
              onError={() => {
                navigate(routeFactory.error.getUrlPath({}));
              }}
            />
          }
        />
        <Route path={routeFactory.oidcLogout.routePath} element={<OIDCLogout />} />
        <Route
          path={routeFactory.oidcLoginCallback.routePath}
          element={
            <OIDCLoginCallback
              onSuccess={returnUrl => {
                navigate(returnUrl || routeFactory.root.getUrlPath());
              }}
            />
          }
        />
        <Route
          path={routeFactory.oidcLogoutCallback.routePath}
          element={
            <OIDCLogoutCallback
              onSuccess={() => {
                navigate(routeFactory.loggedOut.getUrlPath({}));
              }}
            />
          }
        />
        {/* calendar renders its own nested <Routes> for sub-tabs, so the parent mount needs the /* wildcard */}
        <Route
          path={`${routeFactory.calendar.routePath}/*`}
          element={
            <PrivateRouteContent
              renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
            >
              <Suspense fallback={<Loading />}>
                <CalendarRoute />
              </Suspense>
            </PrivateRouteContent>
          }
        />
        <Route
          path={routeFactory.settings.routePath}
          element={
            <PrivateRouteContent
              renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
            >
              <Suspense fallback={<Loading />}>
                <SettingsRoute />
              </Suspense>
            </PrivateRouteContent>
          }
        />
        {/* myOfferings renders its own nested <Routes> for sub-tabs, so the parent mount needs the /* wildcard */}
        <Route
          path={`${routeFactory.myOfferings.routePath}/*`}
          element={
            <PrivateRouteContent
              renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
              accessCheck={() => hasMyOfferings}
            >
              <Suspense fallback={<Loading />}>
                <MyOfferingsRoute />
              </Suspense>
            </PrivateRouteContent>
          }
        />
        {/* myOrders appends /offering-side-panel/... to its own path when opening the side panel, so the parent mount needs the /* wildcard */}
        <Route
          path={`${routeFactory.myOrders.routePath}/*`}
          element={
            <PrivateRouteContent
              requiredPermissions={[
                permissionsByEntity.CoveredAccountsInstitutionalIndication.READ,
              ]}
              renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
            >
              <Suspense fallback={<Loading />}>
                <MyOrdersRoute />
              </Suspense>
            </PrivateRouteContent>
          }
        />
        <Route
          path={routeFactory.offeringCreate.routePath}
          element={
            <PrivateRouteContent
              requiredPermissions={[permissionsByEntity.Offering.FULL]}
              renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
            >
              <OfferingCreateRoute />
            </PrivateRouteContent>
          }
        />
        {/* offering renders its own nested <Routes> for every offering sub-page, so the parent mount needs the /* wildcard */}
        <Route
          path={`${routeFactory.offering.routePath}/*`}
          element={
            <PrivateRouteContent
              renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
            >
              <Suspense fallback={<Loading />}>
                <OfferingRoute />
              </Suspense>
            </PrivateRouteContent>
          }
        />
        <Route
          path={routeFactory.sharedDraftOffering.routePath}
          element={
            <PrivateRouteContent
              requiredPermissions={[permissionsByEntity.Offering.READ]}
              renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
            >
              <Suspense fallback={<Loading />}>
                <SharedDraftOfferingRoute />
              </Suspense>
            </PrivateRouteContent>
          }
        />
        {/* certificateLibrary renders its own nested <Routes> for the side panel, so the parent mount needs the /* wildcard */}
        <Route
          path={`${routeFactory.certificateLibrary.routePath}/*`}
          element={
            <PrivateRouteContent
              renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
              accessCheck={() =>
                canRenderCertificateLibraryRoute || canRenderCertificateReviewRoute
              }
              requiredPermissions={[
                ...certificateLibraryPermissions,
                ...certificateReviewPermissions,
              ]}
              requireAllPermissions={false}
            >
              <Suspense fallback={<Loading />}>
                <CertificateLibraryRoute />
              </Suspense>
            </PrivateRouteContent>
          }
        />
        {/* investorDirectory renders its own nested <Routes> for the firm detail view, so the parent mount needs the /* wildcard */}
        <Route
          path={`${routeFactory.investorDirectory.routePath}/*`}
          element={
            <PrivateRouteContent
              renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
              accessCheck={() => canRenderInvestorDirectoryRoute}
              requiredPermissions={[permissionsByEntity.Investor.READ]}
            >
              <Suspense fallback={<Loading />}>
                <InvestorDirectoryRoute />
              </Suspense>
            </PrivateRouteContent>
          }
        />
        <Route
          path={routeFactory.browserNotificationDocumentation.routePath}
          element={
            <PrivateRouteContent
              renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
            >
              <Suspense fallback={<Loading />}>
                <BrowserNotificationsDocumentationRoute />
              </Suspense>
            </PrivateRouteContent>
          }
        />
        <Route path={routeFactory.error.routePath} element={<ErrorRoute />} />
        <Route
          path={routeFactory.syndicateWiresInvitationWireResponse.routePath}
          element={
            <Suspense fallback={<Loading />}>
              <InvitationWireResponseRoute />
            </Suspense>
          }
        />
        <Route
          path="*"
          element={
            <PrivateRouteContent
              renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
            >
              <Navigate
                to={
                  hasMyOfferings
                    ? routeFactory.myOfferings.getUrlPath()
                    : routeFactory.calendar.getUrlPath()
                }
                replace
              />
            </PrivateRouteContent>
          }
        />
      </Routes>
      {/*
      Side Panels that can be invoked and displayed by multiple different routes should be placed here
    */}
      <CmgEntityKeyProvider>
        <FirmKeyOrIdProvider>
          <OfferingSidePanelContainer />
        </FirmKeyOrIdProvider>
      </CmgEntityKeyProvider>

      {/* Modals shared accross multiple routes */}
      <SupportRequestModal />
      <CrmSelectModal />
    </React.Fragment>
  );
};

export default RootRouter;
